import React from "react";

/**
 * The shop hero — one implementation for every page that presents a shop (or a professional) as a
 * storefront: the shop page, its services page, its packages page, and the R3/R4/R7 professional page.
 *
 * Extracted 2026-11-03 because the pages had drifted: the shop hero was 240px minimum with content-driven
 * height, while the professional hero hard-coded 360px. Two copies of "the same" banner meant the shop
 * looked right and the professional page looked like a different site.
 *
 * `actions` lets each page supply its own calls to action without forking the layout:
 *   [{ label, onClick, variant: "primary" | "solid" | "ghost", hideOnMobile?, hideOnDesktop?, mobileLabel? }]
 */
const ShopHero = ({
  name,
  locationLine,
  contactLine,
  logoUrl,
  backgroundImage,
  schedule,
  isMobile,
  actions = [],
}) => {
  const visibleActions = (actions || []).filter((action) => {
    if (isMobile && action.hideOnMobile) return false;
    if (!isMobile && action.hideOnDesktop) return false;
    return true;
  });

  const buttonStyles = (variant) => {
    const base = {
      borderRadius: isMobile ? "9px" : "10px",
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.25s",
      flex: "0 0 auto",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    if (variant === "primary") {
      return {
        ...base,
        padding: isMobile ? "10px 18px" : "10px 22px",
        background: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)",
        color: "white",
        border: "none",
        boxShadow: "0 3px 10px rgba(245, 158, 11, 0.35)",
      };
    }
    if (variant === "ghost") {
      return {
        ...base,
        padding: isMobile ? "10px 14px" : "10px 22px",
        background: isMobile
          ? "rgba(255,255,255,0.14)"
          : "rgba(255,255,255,0.16)",
        color: "white",
        border: "1.5px solid rgba(255,255,255,0.75)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.22)",
        backdropFilter: "blur(4px)",
      };
    }
    return {
      ...base,
      padding: isMobile ? "10px 18px" : "10px 22px",
      background: "white",
      color: "#2d6da3",
      border: "2px solid white",
      boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
    };
  };

  const hoursEntries =
    schedule && Object.keys(schedule).length > 0
      ? Object.entries(schedule)
      : [];

  return (
    <div
      className="shop-hero"
      style={{
        background: backgroundImage
          ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url("${backgroundImage}") center/cover`
          : "var(--ogestyle-storefront-hero-blue)",
        // Height follows the content. A fixed box clipped the hero's own content on mobile because
        // ShopLanding.css sets overflow:hidden.
        height: "auto",
        minHeight: isMobile ? "0" : "240px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        position: "relative",
        // Bottom padding reserves the strip the mobile hours card occupies.
        padding: isMobile ? "28px 20px 104px" : "26px 60px",
        // The site header is `position: fixed` with a 50px bar, so the hero starts below it.
        marginTop: "calc(var(--demo-banner-height, 0px) + 50px)",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          width: "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: isMobile ? "center" : "space-between",
          gap: isMobile ? "12px" : "20px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Brand logo (25%) */}
        {logoUrl && (
          <div
            style={{
              flex: isMobile ? "0 0 auto" : "0 0 25%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={logoUrl}
              alt={name}
              style={{
                // Circular badge, artwork scaled to fit INSIDE the circle rather than masked by it:
                // a circle only contains the square inscribed in it, so an edge-to-edge wordmark
                // loses its corners.
                width: isMobile ? "130px" : "150px",
                height: isMobile ? "130px" : "150px",
                borderRadius: "50%",
                objectFit: "contain",
                objectPosition: "center",
                background: "#0d0d0d",
                padding: isMobile ? "17px" : "20px",
                boxSizing: "border-box",
                border: "3px solid rgba(255, 255, 255, 0.9)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            />
          </div>
        )}

        {/* Name, place, contact, actions (50%) */}
        <div
          style={{
            flex: isMobile ? "1" : logoUrl ? "0 0 50%" : "1",
            textAlign: "center",
            minWidth: 0,
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "17px" : "48px",
              fontWeight: "700",
              margin: isMobile ? "0 0 4px 0" : "0 0 16px 0",
              textShadow: "0 2px 4px rgba(0,0,0,0.5)",
              lineHeight: 1.15,
            }}
          >
            {name}
          </h1>

          {locationLine && (
            <p
              style={{
                fontSize: isMobile ? "13px" : "20px",
                margin: isMobile ? "0 0 1px 0" : "0 0 8px 0",
                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
              }}
            >
              📍 {locationLine}
            </p>
          )}

          {contactLine && (
            <p
              style={{
                fontSize: isMobile ? "13px" : "16px",
                margin: 0,
                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
              }}
            >
              {contactLine}
            </p>
          )}

          {visibleActions.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: isMobile ? "8px" : "10px",
                marginTop: isMobile ? "22px" : "32px",
                justifyContent: "center",
                alignItems: "center",
                flexWrap: isMobile ? "nowrap" : "wrap",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {visibleActions.map((action, index) => (
                <button
                  key={`${action.label}-${index}`}
                  type="button"
                  onClick={action.onClick}
                  style={buttonStyles(action.variant)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {isMobile && action.mobileLabel
                    ? action.mobileLabel
                    : action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Business hours (25%) — desktop only */}
        {!isMobile && hoursEntries.length > 0 && (
          <div
            style={{
              flex: "0 0 25%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: "12px",
                padding: "16px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                width: "100%",
                maxWidth: "220px",
              }}
            >
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#111827",
                  marginBottom: "12px",
                  borderBottom: "2px solid #2d6da3",
                  paddingBottom: "6px",
                }}
              >
                🕐 Business Hours
              </div>
              {hoursEntries.map(([day, hours]) => (
                <div
                  key={day}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 0",
                    fontSize: "11px",
                  }}
                >
                  <span style={{ color: "#111827" }}>{day.substring(0, 3)}</span>
                  <span
                    style={{
                      color: !hours.active ? "#ef4444" : "#10b981",
                      fontWeight: "500",
                    }}
                  >
                    {!hours.active ? "Closed" : `${hours.open} - ${hours.close}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Mobile business hours pill */}
      {isMobile && hoursEntries.length > 0 && (
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "8px",
            padding: "8px 12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            maxWidth: "95%",
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontSize: "9px",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "4px",
              textAlign: "center",
            }}
          >
            🕐 Business Hours
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "4px 8px",
              fontSize: "10px",
            }}
          >
            {hoursEntries.map(([day, hours]) => (
              <span
                key={day}
                style={{
                  color: !hours.active ? "#ef4444" : "#10b981",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                }}
              >
                {day.substring(0, 3)}:{" "}
                {!hours.active ? "Closed" : `${hours.open}-${hours.close}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopHero;
